import { map, pathOr } from 'ramda';
import ReactGA from 'react-ga';
import { LandscapeState } from '@wayfinder/vms-state';

const TRACK_ID = 'UA-142300865-1';

const ACTIONS = {
    LOGIN: 'Login',
    RESET: 'Reset Password',
    LOGOUT: 'Logout',
    CLICK: 'Click',
    UPDATE: 'Update',
    CLOSE: 'Close',
    VENUE_LIST: 'Venue List',
    POI_SHEET: 'POI Sheet',
    MAPNOTE_SHEET: 'Map Note Sheet',
    MAP: 'Map',
    PUBLISH: 'Publish',
    LIST: 'List',
    IMPORT: 'Import',
    RELOAD: 'Reload',
    EMAIL_SUPPORT: 'EmailSupport',
    HOVER: 'Hover',
    CLICK_DOWNLOAD_POI: 'Clicked Download POIs Button',
};

const LABELS = {
    CLICK: 'Click',
    REMOVE: 'Remove',
    CONFIRM_REMOVE: 'Confirm Remove',
    CANCEL_REMOVE: 'Cancel Remove',
    EDIT: 'Edit',
    SAVE: 'Save',
    VIEW: 'View',
    CLICK_POI: 'Click POI',
    ADD_BUTTON: 'Add Button Clicked',
    ADD_POINT_POI: 'Add Point POI',
    ADD_MAPNOTE: 'Add Map Note',
    CLICK_MAPNOTE: 'Click Map Note',
    CHANGE_LEVEL: 'Change Level',
    HOVER_DOWNLOAD_POI_BUTTON: 'Hover over Download POIs',
    CLICK_DOWNLOAD_POI: 'Clicked Download POIs Button',
};

const CATEGORIES = {
    SETTINGS: 'Settings',
    ERROR_PAGE: 'Error Page',
};

const initialize = userId => {
    const options = { gaOptions: { userId } };

    ReactGA.initialize(TRACK_ID, options);
    ReactGA.pageview(window.location.pathname);

    recordLogin(userId);
};

const createEvent = (category, action, label, value, nonInteraction, transport) => ({
    category,
    action,
    label,
    value,
    nonInteraction,
    transport,
});

const recordEvent = event => ReactGA.event(event);

const recordEntitiesUpdate = (venueId, updates, isBulkUpdate) => {
    const recordEntity = changes => {
        const { entity } = changes;
        const { id } = entity;
        const updatedEntity = LandscapeState.selectors.getPoi(venueId, `${venueId}_${id.replace('#', '')}`);
        const label = `${LABELS.EDIT}: ${updatedEntity.uuid} ${updatedEntity.name}`;
        const event = createEvent(venueId, ACTIONS.POI_SHEET, label);
        recordEvent(event);
    };

    map(recordEntity, updates);
};

const recordSavePoi = (venueId, updatedEntities) => {
    const record = entity => {
        const label = `${LABELS.SAVE}: ${entity.uuid} ${entity.name}`;
        const event = createEvent(venueId, ACTIONS.POI_SHEET, label);
        recordEvent(event);
    };

    map(record, updatedEntities);
};

const recordSaveMapNote = (venueId, noteId) => {
    const event = createEvent(venueId, ACTIONS.MAPNOTE_SHEET, `${LABELS.SAVE}: ${noteId}`);
    recordEvent(event);
};

const recordPoiClick = (venueId, id, query) => {
    const action = query.view && query.view === 'map' ? ACTIONS.MAP : ACTIONS.POI_SHEET;
    const entity = LandscapeState.selectors.getPoi(venueId, id);
    const event = createEvent(venueId, action, `${LABELS.CLICK_POI}: ${id} ${entity.name}`);
    recordEvent(event);
};

const recordAddPoi = venueId => {
    const event = createEvent(venueId, ACTIONS.MAP, LABELS.ADD_POINT_POI);
    recordEvent(event);
};

const recordMapNoteClick = (venueId, noteId) => {
    const event = createEvent(venueId, ACTIONS.MAP, `${LABELS.CLICK_MAPNOTE}: ${noteId}`);
    recordEvent(event);
};

const recordAddMapNote = venueId => {
    const event = createEvent(venueId, ACTIONS.MAP, LABELS.ADD_MAPNOTE);
    recordEvent(event);
};

const recordViewChange = (l, newView) => {
    const venueId = pathOr('', ['payload', 'venueId'], l);
    const action = ACTIONS[newView.toUpperCase()];
    const event = createEvent(venueId, action, LABELS.VIEW);
    recordEvent(event);
};

const recordVenue = (venueId, query) => {
    const event = createEvent(venueId, ACTIONS.VENUE_LIST, LABELS.CLICK);
    recordEvent(event);
};

const recordShowRemoveConfirmation = venueId => {
    const event = createEvent(venueId, ACTIONS.POI_SHEET, LABELS.REMOVE);
    recordEvent(event);
};

const recordHideRemoveConfirmation = (venueId, userResponse) => {
    const label = userResponse ? LABELS.CONFIRM_REMOVE : LABELS.CANCEL_REMOVE;
    const event = createEvent(venueId, ACTIONS.POI_SHEET, label);
    recordEvent(event);
};

const recordAddDropdown = venueId => {
    const event = createEvent(venueId, ACTIONS.MAP, LABELS.ADD_BUTTON);
    recordEvent(event);
};

const recordPublish = venueId => {
    const event = createEvent(venueId, ACTIONS.PUBLISH, LABELS.PUBLISH_CHANGES);
    recordEvent(event);
};

const recordLevelChange = (venueId, levelId) => {
    const event = createEvent(venueId, ACTIONS.MAP, `${LABELS.CHANGE_LEVEL}: ${levelId}`);
    recordEvent(event);
};

const recordHoverDownloadPoiButton = venueId => {
    const event = createEvent(venueId, ACTIONS.HOVER, LABELS.HOVER_DOWNLOAD_POI_BUTTON);
    recordEvent(event);
};

const recordDownloadPois = venueId => {
    const event = createEvent(venueId, ACTIONS.CLICK_DOWNLOAD_POI, LABELS.CLICK_DOWNLOAD_POI);
    recordEvent(event);
};

// Menu ----------------------------------------------------------------------------------------------------------------

const recordMenuClick = () => {
    const event = createEvent(CATEGORIES.SETTINGS, ACTIONS.CLICK);
    recordEvent(event);
};

// Change Profile Modal ------------------------------------------------------------------------------------------------

const recordProfileModalOpen = () => {};

const recordProfileModalClose = () => {};

const recordChangeProfile = () => {};

// Change Password Modal -----------------------------------------------------------------------------------------------

const recordChangePasswordModalOpen = () => {};

const recordChangePasswordModalClose = () => {};

const recordChangePassword = () => {};

// Support Modal -------------------------------------------------------------------------------------------------------

const recordSupportModalOpen = () => {};

// Login / Logout ------------------------------------------------------------------------------------------------------

const recordLogin = userId => {
    const event = createEvent(CATEGORIES.SETTINGS, ACTIONS.LOGIN, userId);
    recordEvent(event);
};

const recordResetPassword = () => {
    const event = createEvent(CATEGORIES.SETTINGS, ACTIONS.RESET);
    recordEvent(event);
};

const recordLogout = userId => {
    const event = createEvent(CATEGORIES.SETTINGS, ACTIONS.LOGOUT, userId);
    recordEvent(event);
};

// Error Page

const recordReloadVenuesButtonClick = userId => {
    const event = createEvent(CATEGORIES.ERROR_PAGE, ACTIONS.RELOAD, userId);
    recordEvent(event);
};

const recordEmailSupportButtonClick = userId => {
    const event = createEvent(CATEGORIES.ERROR_PAGE, ACTIONS.EMAIL_SUPPORT, userId);
    recordEvent(event);
};

export {
    ACTIONS,
    LABELS,
    createEvent,
    initialize,
    recordAddDropdown,
    recordAddMapNote,
    recordAddPoi,
    recordDownloadPois,
    recordEmailSupportButtonClick,
    recordEntitiesUpdate,
    recordEvent,
    recordHideRemoveConfirmation,
    recordHoverDownloadPoiButton,
    recordLevelChange,
    recordLogout,
    recordMapNoteClick,
    recordMenuClick,
    recordPoiClick,
    recordPublish,
    recordReloadVenuesButtonClick,
    recordResetPassword,
    recordSaveMapNote,
    recordSavePoi,
    recordShowRemoveConfirmation,
    recordVenue,
    recordViewChange,
};
